import { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { MdClose } from 'react-icons/md';
import { PiCalendarBlankDuotone } from 'react-icons/pi';
import { TbFileSpreadsheet, TbFileTypeCsv } from 'react-icons/tb';
import { format, subDays, subMonths, subWeeks, subYears } from 'date-fns';

import FuseLoading from '@fuse/core/FuseLoading';
import { makeStyles } from '@material-ui/core/styles';
import { DatePicker } from '@material-ui/pickers';

import { useOptimizationPipeline } from 'app/admin/optimizationPipelineV2/component/pipeline/providers/OptimizationPipelineProvider';
import { getSummaryResolution } from 'app/admin/optimizationPipelineV2/component/pipeline/utils/dateTime';

const usePopupDatePickerStyles = makeStyles(() => ({
    root: { width: 95, margin: 0 },
    outlinedRoot: {
        overflow: 'hidden',
        backgroundColor: '#ffffff',
        borderRadius: 8,
        boxShadow: 'inset 0 2px 4px 0 rgba(0,0,0,0.06)',
        height: 28,
        display: 'flex',
        alignItems: 'center',
        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0', borderWidth: 1 }
    },
    input: { fontSize: 11, color: '#616161', padding: 0, textAlign: 'center', height: 28, boxSizing: 'border-box', cursor: 'pointer' }
}));

const DEFAULT_QUICK_OPTIONS = [
    { value: 'ALL', label: 'All' },
    { value: '1Y', label: '1 Year' },
    { value: '1M', label: '1 Month' },
    { value: '1W', label: '1 Week' },
    { value: '1D', label: '1 Day' }
];

const POPUP_DATE_PICKER_LAYER_STYLE = { zIndex: 4000 };

function formatInputDate(date) {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
    return format(date, 'yyyy-MM-dd');
}

function parseInputDate(value) {
    if (!value) return null;
    const date = new Date(value + 'T00:00:00');
    if (Number.isNaN(date.getTime())) return null;
    return date;
}

function resolveQuickRange(nextQuickRange, minDate, maxDate) {
    const anchorDate = maxDate instanceof Date && !Number.isNaN(maxDate.getTime()) ? maxDate : new Date();

    if (nextQuickRange === 'ALL') return { from: minDate ? formatInputDate(minDate) : '', to: maxDate ? formatInputDate(maxDate) : '', quickRange: 'ALL' };
    if (nextQuickRange === '1Y') return { from: formatInputDate(subYears(anchorDate, 1)), to: formatInputDate(anchorDate), quickRange: '1Y' };
    if (nextQuickRange === '1M') return { from: formatInputDate(subMonths(anchorDate, 1)), to: formatInputDate(anchorDate), quickRange: '1M' };
    if (nextQuickRange === '1W') return { from: formatInputDate(subWeeks(anchorDate, 1)), to: formatInputDate(anchorDate), quickRange: '1W' };
    if (nextQuickRange === '1D') return { from: formatInputDate(subDays(anchorDate, 1)), to: formatInputDate(anchorDate), quickRange: '1D' };

    return { from: minDate ? formatInputDate(minDate) : '', to: maxDate ? formatInputDate(maxDate) : '', quickRange: nextQuickRange };
}

export default function OptimizationPipelinePopup(props) {
    const { open, onClose, children, className, width = '1100px', minWidth = 320, maxWidth = 1200, overflowVisible = false } = props;

    const [visible, setVisible] = useState(false);
    const [mounted, setMounted] = useState(false);

    useEffect(() => { setMounted(true); }, []);

    useEffect(() => {
        if (!open) { setVisible(false); return undefined; }
        const timeout = setTimeout(() => { setVisible(true); }, 10);
        return () => { clearTimeout(timeout); };
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const handleKeyDown = (event) => { if (event.key === 'Escape') onClose?.(); };
        window.addEventListener('keydown', handleKeyDown);
        return () => { window.removeEventListener('keydown', handleKeyDown); };
    }, [open, onClose]);

    useEffect(() => {
        if (!open) return undefined;
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = originalOverflow; };
    }, [open]);

    if (!open || !mounted || typeof document === 'undefined') return null;

    return ReactDOM.createPortal(
        <div className='fixed inset-0' style={{ zIndex: 3000 }}>

            <div className={'fixed inset-0 bg-black bg-opacity-30 transition-opacity duration-200 ' + (visible ? 'opacity-100' : 'opacity-0')} style={{ zIndex: 3000 }} onClick={onClose} />

            <div className='fixed inset-0 flex items-center justify-center px-16 sm:px-32 pointer-events-none' style={{ zIndex: 3100 }}>
                <div className={'w-full min-h-0 flex flex-col rounded-16 border border-gray-200 bg-white shadow-2xl transition-all duration-200 ease-in-out pointer-events-auto ' + (overflowVisible ? 'overflow-visible ' : 'overflow-hidden ') + (visible ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-8 transform') + (className ? ' ' + className : '')} style={{ width: width, minWidth: minWidth, maxWidth: maxWidth, maxHeight: 'calc(100vh - 96px)' }}>
                    {children}
                </div>
            </div>

        </div>,
        document.body
    );
}

export function OptimizationPipelinePopupHeader(props) {
    const { title, subtitle, icon: Icon, onClose, children } = props;
    const { ts, optimizationPipelineData } = useOptimizationPipeline();

    const fallbackSubtitle = /*ts ? 'Detailed data for ' + format(new Date(ts), getSummaryResolution(optimizationPipelineData?.summaryDurationUnit) === 'day' ? 'yyyy.MM.dd.' : 'yyyy.MM.dd. HH:mm') :*/ 'Detailed history data';
    const resolvedSubtitle = subtitle ?? fallbackSubtitle;

    return (
        <div className='w-full shrink-0 flex items-center justify-between gap-12 px-16 py-12 bg-gray-100 rounded-t-16 border-b border-gray-200'>
            <div className='min-w-0 flex items-center gap-10'>
                {Icon && <div className='shrink-0 text-20 text-gray-700'><Icon /></div>}

                <div className='min-w-0 flex flex-col'>
                    <div className='text-14 font-semibold text-gray-900 truncate'>{title}</div>
                    {resolvedSubtitle && <div className='text-11 text-gray-500 truncate'>{resolvedSubtitle}</div>}
                </div>
            </div>

            <div className='shrink-0 flex items-center gap-8'>
                {children}

                <button type='button' className='w-32 h-32 flex items-center justify-center rounded-full hover:bg-gray-200 transition-colors duration-200' onClick={onClose}>
                    <MdClose className='text-18 text-gray-700' />
                </button>
            </div>
        </div>
    );
}

export function PopupBody(props) {
    const { children, className, padding = true, scroll = true } = props;

    return (
        <div className={'w-full min-h-0 flex-1 flex flex-col ' + (scroll ? 'overflow-auto' : 'overflow-hidden') + (padding ? ' p-16' : '') + (className ? ' ' + className : '')}>
            {children}
        </div>
    );
}

export function PopupDateRangeQuickSelector(props) {
    const { value, onChange, quickOptions = DEFAULT_QUICK_OPTIONS, className, minDate = null, maxDate = null, disabledQuickOptions = {} } = props;

    const from = value?.from || '';
    const to = value?.to || '';
    const quickRange = value?.quickRange || 'ALL';

    function handleQuickRangeClick(nextQuickRange) {
        if (disabledQuickOptions?.[nextQuickRange]) return;
        onChange?.({ ...value, ...resolveQuickRange(nextQuickRange, minDate, maxDate) });
    }

    function handleFromChange(date) {
        onChange?.({ ...value, from: formatInputDate(date), quickRange: null });
    }

    function handleToChange(date) {
        onChange?.({ ...value, to: formatInputDate(date), quickRange: null });
    }

    return (
        <div className={'flex items-center gap-8 ' + (className || '')}>
            <div className='hidden lg:flex items-center gap-4'>
                {quickOptions.map((option) => {
                    const active = quickRange === option.value;
                    const disabled = !!disabledQuickOptions?.[option.value];

                    return (
                        <button key={option.value} type='button' disabled={disabled} className={'h-28 px-12 rounded-lg border text-11 font-medium transition-colors duration-200 ' + (active ? 'bg-black text-white border-black' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100 shadow-inner') + (disabled ? ' opacity-40 cursor-not-allowed hover:bg-white' : '')} onClick={() => { handleQuickRangeClick(option.value); }}>
                            {option.label}
                        </button>
                    );
                })}
            </div>

            <div className='flex items-center gap-6'>
                <div className='w-20 h-20 flex items-center justify-center text-gray-500'><PiCalendarBlankDuotone className='text-16' /></div>
                <PopupDatePickerField value={parseInputDate(from)} onChange={handleFromChange} minDate={minDate || undefined} maxDate={maxDate || undefined} />
                <div className='text-12 text-gray-500'>-</div>
                <PopupDatePickerField value={parseInputDate(to)} onChange={handleToChange} minDate={minDate || undefined} maxDate={maxDate || undefined} />
            </div>
        </div>
    );
}

function PopupDatePickerField(props) {
    const classes = usePopupDatePickerStyles();

    return (
        <DatePicker className={classes.root} label={null} margin='dense' value={props.value ?? undefined} onChange={props.onChange} ampm={false} minDate={props.minDate} maxDate={props.maxDate} format='yyyy-MM-dd' cancelLabel='Back' okLabel='Ok' clearLabel='Clear' inputVariant='outlined' clearable={false} InputProps={{ classes: { root: classes.outlinedRoot, input: classes.input } }} PopoverProps={{ style: POPUP_DATE_PICKER_LAYER_STYLE }} DialogProps={{ style: POPUP_DATE_PICKER_LAYER_STYLE }} />
    );
}

export function PopupLoader(props) {
    const { label = 'Loading data...', className, minHeight = 240 } = props;

    return (
        <div className={'w-full flex items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 ' + (className || '')} style={{ minHeight: minHeight }}>
            <FuseLoading label={label} />
        </div>
    );
}

export function PopupToolbar(props) {
    const { children, className } = props;

    return (
        <div className={'w-full shrink-0 flex items-center justify-between gap-16 px-16 py-12 border-b border-gray-200 bg-white ' + (className || '')}>
            {children}
        </div>
    );
}

export function PopupToolbarGroup(props) {
    const { label, children, className } = props;

    return (
        <div className={'flex items-center gap-4 py-6 ' + (className || '')}>
            {label ? <div className='text-11 leading-none opacity-70 mr-4 whitespace-nowrap'>{label}</div> : null}
            <div className='flex items-center gap-5'>{children}</div>
        </div>
    );
}

export function PopupToolbarScrollGroup(props) {
    const { label, children, className } = props;

    return (
        <div className={'flex items-center gap-8 overflow-x-auto whitespace-nowrap pr-4 py-4 ' + (className || '')}>
            {label ? <div className='text-11 opacity-70 mr-4 flex-shrink-0'>{label}</div> : null}
            {children}
        </div>
    );
}

export function PopupToolbarChip(props) {
    const { label, onClick, disabled = false, active = false, className } = props;

    return (
        <button type='button' onClick={onClick} disabled={disabled} aria-pressed={active} className={'text-10 whitespace-nowrap leading-none font-medium px-10 py-5 rounded border transition-colors ' + (active ? 'bg-black text-white border-black' : 'bg-transparent text-black/70 border-gray-100 hover:bg-gray-50') + (disabled ? ' opacity-40 cursor-not-allowed' : ' ') + (className ? ' ' + className : '')}>
            {label}
        </button>
    );
}

export function PopupHeaderButton(props) {
    const { label, icon: Icon, onClick, disabled = false, title, className = '' } = props;

    return (
        <button
            type='button'
            title={title}
            disabled={disabled}
            className={'flex items-center gap-4 h-28 px-12 rounded-lg border text-11 font-medium transition-colors duration-200 bg-white text-gray-700 border-gray-300 hover:bg-gray-100 ' + (disabled ? 'opacity-40 cursor-not-allowed hover:bg-white' : '') + (className ? ' ' + className : '')}
            onClick={onClick}
        >
            {Icon && <Icon className='text-14 shrink-0' />}
            <span>{label}</span>
        </button>
    );
}

export function PopupExportButtons(props) {
    const { onExcel, onCsv, disabled = false } = props;

    return (
        <>
            <PopupHeaderButton label='Excel' icon={TbFileSpreadsheet} disabled={disabled} onClick={onExcel} />
            <PopupHeaderButton label='CSV' icon={TbFileTypeCsv} disabled={disabled} onClick={onCsv} />
        </>
    );
}

export function PopupActionButton(props) {
    const { label, icon: Icon, className = '', disabled = false, onClick } = props;

    return (
        <button className={'flex items-center justify-center gap-6 transition rounded-full px-16 py-8 shadow-sm border bg-white ' + className + (disabled ? ' filter grayscale cursor-not-allowed' : '')} onClick={onClick} disabled={disabled}>
            {Icon && <div className={'text-sm mb-1 ' + (disabled ? 'opacity-50' : '')}><Icon /></div>}
            <div className={'text-xs font-medium text-center whitespace-nowrap ' + (disabled ? 'opacity-50' : '')}>{label}</div>
        </button>
    );
}
